<template>
  <RAFTFullScreenContent
    header-text="Bio Material Exposures"
    class="bio-space-listing"
  >
    <template #topleft>
      <RaftBusy busy-text="Generating file" v-if="isDownloading" />
      <VActionButton
        v-else
        :show-badge="false"
        :show-icon="false"
        label="Download History"
        @click="downloadExposureListing"
      />
    </template>
    <BaseFilters
      @update-filters="updateFilters"
      :filters="filters"
      :is-busy="!componentReady"
    />
    <BaseTable
      v-if="componentReady"
      :items="items"
      :fields="fields"
      :total-items="totalMaterials"
      :current-page="currentPageValue"
      :is-busy="isBusy"
      @update-current-page="updateCurrentPage"
      ref="table"
      :no-local-sorting="false"
    >
      <template #empty>
        <div class="empty-text">
          There are currently no biological materials to list.
        </div>
      </template>
      <template #internal-link="row">
        <a :href="row.data.value.url">{{ row.data.value.text }}</a>
      </template>
      <template #biomaterial="row">
        {{ row?.data?.value?.name }}
        <BioMaterialDataWindow
          :material-type="row?.data?.value?.type?.substring(14)"
          :bio-uuid="row?.data?.value?.id"
          :material-type-label="row?.data?.value?.bio_material_type?.label"
          :additional-id="String(row?.data?.index)"
        />
      </template>
      <template #project="row">
        <b-link :href="'/node/' + row.data.value?.drupal_internal__nid">
          {{ row?.data?.value?.title }}
        </b-link>
      </template>
      <template #group="row">
        <b-link :href="'/node/' + row.data.value[0]?.drupal_internal__nid">
          {{ row?.data?.value?.[0]?.title }}
        </b-link>
      </template>
      <template #researcher="row">
        <b-link :href="'/user/' + row.data.value?.drupal_internal__uid">
          {{ row?.data?.value?.display_name }}
        </b-link>
      </template>
      <template #space="row">
        {{ row?.data?.value?.length }}
      </template>
    </BaseTable>
  </RAFTFullScreenContent>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { jsonClient, restClient } from "@/lib/raftApi";
import JsonApiFilterManager from "@/lib/JsonApiFilterManager";
import RaftBusy from "@/components/RaftBusy.vue";
import { useDrupalSettings } from "@/composables/useDrupalSettings";
import { cloneDeep } from "lodash";
import BaseFilters from "../raft/BaseFilters.vue";
import BaseTable from "../raft/BaseTable.vue";
import BioMaterialDataWindow from "./BioMaterialDataWindow.vue";
import RAFTFullScreenContent from "../RAFTFullScreenContent.vue";
import VActionButton from "../raft/VActionButton.vue";

const { config } = useDrupalSettings();

export default {
  name: "BioSpaceListing",
  components: {
    RaftBusy,
    BaseFilters,
    BaseTable,
    BioMaterialDataWindow,
    RAFTFullScreenContent,
    VActionButton,
  },
  props: {
    spaceNid: {
      type: [String, Number],
      required: false,
      default: null,
    },
    userUid: {
      type: [String, Number],
      required: false,
      default: null,
    },
  },
  data() {
    const appData = {
      baseQueries: {
        include:
          "field_bio_project_spaces,field_bio_project_material.association,field_bio_project_material.association.og_audience,field_bio_project_material.field_bio_material,field_bio_project_material.field_bio_material.og_audience,field_bio_project_material.field_bio_material.bio_material_type,user_id",
        "filter[status]": 1,
        "filter[operative_status]": 1,
        "fields[node--bio_research_project]":
          "drupal_internal__nid,title,og_audience",
        "fields[node--laboratory]": "drupal_internal__nid,title",
        "fields[node--space]": "drupal_internal__nid,title",
        "fields[raft_metadata--bio_project_material]":
          "association,field_bio_material",
        "fields[user--user]":
          "display_name,field_prof_display_name,drupal_internal__uid",
      },
      items: [],
      currentPage: 1,
      error: "",
      isBusy: false,
      isDownloading: false,
      componentReady: false,
      materialsPerPage: 25,
      response: {},
      totalMaterials: 0,
      debounceUpdateBioMaterials: (() => this.updateBioMaterials(), 300),
      sortBy: null,
    };
    if (!this.sortBy) {
      appData.baseQueries.sort =
        "field_bio_project_material.field_bio_material.type.meta.drupal_internal__target_id,field_bio_project_material.field_bio_material.name";
    }
    return appData;
  },
  computed: {
    console: () => console,
    ...mapState("BiologicalsStore", ["userExposures"]),
    ...mapGetters("BiologicalsStore", [
      "getBiomaterialTypeOptions",
      "getContainmentLevels",
      "getContainmentLevelOptions",
      "getBioMaterialsThatSupportPlasmidVectors",
      "getProjectOptions",
      "getResearcherOptions",
      "getGroupOptions",
      "getSpaceOptions",
      "getRiskLevelsLabel",
    ]),
    currentPageValue: function () {
      return this.currentPage ? this.currentPage : 1;
    },
    fields: function () {
      const tableFields = [
        {
          key: "field_bio_project_material.field_bio_material.bio_material_type.label",
          label: "Material Type",
          sortable: true,
        },
        {
          key: "field_bio_project_material.field_bio_material",
          label: "Material Name",
          sortable: true,
          custom_slot: "biomaterial",
        },
        {
          key: "field_bio_project_material.field_bio_material.research_containment_bsl",
          label: this.getRiskLevelsLabel,
          sortable: true,
          formatter: (value) => this.getContainmentLevels[value],
        },
        {
          key: "field_bio_material_exposure",
          label: "Exposure",
          sortable: true,
          formatter: (value) => this.userExposures[value],
        },
        {
          key: "field_bio_project_material.association",
          label: "Project Name",
          sortable: true,
          custom_slot: "project",
        },
        {
          key: "field_bio_project_material.association.og_audience",
          label: "Group Name",
          sortable: true,
          custom_slot: "group",
        },
      ];
      if (!this.spaceNid) {
        tableFields.push({
          key: "field_bio_project_spaces",
          label: "Spaces",
          sortable: true,
          custom_slot: "space",
        });
      }
      if (!this.userUid) {
        tableFields.push({
          key: "user_id",
          label: "Researcher",
          sortable: true,
          custom_slot: "researcher",
        });
      }
      return tableFields;
    },
    filters: function () {
      const filterDefinitions = [
        {
          key: "field_bio_project_material.field_bio_material.name",
          label: "Material Name",
          type: "string",
        },
        {
          key: "field_bio_project_material.field_bio_material.type.meta.drupal_internal__target_id",
          label: "Material Type",
          type: "list_string",
          definition: {
            settings: { allowed_values: this.getBiomaterialTypeOptions },
          },
          optionsSet: true,
        },
        {
          key: "field_bio_project_material.association.id",
          label: "Project",
          type: "list_string",
          definition: { settings: { allowed_values: this.getProjectOptions } },
          optionsSet: false,
        },
        {
          key: "field_bio_project_material.association.og_audience.id",
          label: "Group",
          type: "list_string",
          definition: { settings: { allowed_values: this.getGroupOptions } },
          optionsSet: false,
        },
        {
          key: "field_bio_project_material.field_bio_material.research_containment_bsl",
          label: `Risk / ${this.getRiskLevelsLabel}`,
          type: "list_string",
          definition: {
            settings: { allowed_values: this.getContainmentLevelOptions },
          },
          optionsSet: true,
        },
      ];
      if (!this.userUid) {
        filterDefinitions.push({
          key: "user_id.id",
          label: "Researcher",
          type: "list_string",
          definition: {
            settings: { allowed_values: this.getResearcherOptions },
          },
          optionsSet: true,
        });
      }
      if (!this.spaceNid) {
        filterDefinitions.push({
          key: "field_bio_project_spaces.id",
          label: "Space",
          type: "list_string",
          definition: { settings: { allowed_values: this.getSpaceOptions } },
          optionsSet: true,
        });
      }
      return filterDefinitions;
    },
  },
  methods: {
    ...mapActions("BiologicalsStore", [
      "fetchBioSpaceListingOptions",
      "fetchUserExposureEntityDisplayData",
    ]),
    async updateBioMaterials() {
      this.isBusy = true;
      if (this.currentPage > 1) {
        JsonApiFilterManager.setPageOffset(
          (this.currentPage - 1) * this.materialsPerPage,
        );
      } else {
        JsonApiFilterManager.setPageOffset(0);
      }
      const options = {
        path: "raft_metadata/bio_material_user_exposure",
        queries: JsonApiFilterManager.queries,
      };
      const response = await jsonClient
        .get(options)
        .catch((error) => this.$emit("space-listing-error", error));
      this.items = response.deserialized;
      this.totalMaterials = parseInt(response?.meta?.count ?? 0, 10);
      this.isBusy = false;
      this.componentReady = true;
    },
    setDefaultFilters() {
      JsonApiFilterManager.queries = this.baseQueries;
      JsonApiFilterManager.queries["page[limit]"] = this.materialsPerPage;
      this.biomaterialTypeOptions?.forEach((material) => {
        JsonApiFilterManager.queries[
          "fields[bio_material--" + material.value + "]"
        ] = "name,bio_material_type,research_containment_bsl";
      });
      JsonApiFilterManager.addFilter("status", "status", 1).addFilter(
        "operative_status",
        "operative_status",
        1,
      );
      if (this.spaceNid) {
        JsonApiFilterManager.addFilter(
          "field_bio_project_spaces",
          "field_bio_project_spaces.meta.drupal_internal__target_id",
          this.spaceNid,
        );
      }
      if (this.userUid) {
        JsonApiFilterManager.addFilter(
          "user",
          "user_id.drupal_internal__uid",
          this.userUid,
        );
      }
    },
    updateCurrentPage(newPage) {
      this.currentPage = newPage;
      this.updateBioMaterials();
    },
    sortingChanged(ctx) {
      this.sortDesc = !ctx.sortDesc;
      const sortDir = this.sortDesc ? "-" : "";
      this.sortBy = sortDir + ctx.sortBy;
      JsonApiFilterManager.setSortFields(this.sortBy);
      this.updateBioMaterials();
    },
    updateFilters(filterValues) {
      Object.keys(filterValues)?.forEach((fieldName) => {
        const filterOpts = filterValues[fieldName];
        if (Array.isArray(filterOpts.value)) {
          if (filterOpts.value.includes(0) || filterOpts.value.length === 0) {
            JsonApiFilterManager.deleteFiltersByName([filterOpts.name]);
          } else {
            JsonApiFilterManager.addMultivalueNamedFilter(
              filterOpts.name,
              fieldName,
              filterOpts.value,
            );
          }
        } else if (filterOpts.value === null || filterOpts.value === "") {
          JsonApiFilterManager.deleteFiltersByName([filterOpts.name]);
        } else {
          JsonApiFilterManager.addFilter(
            filterOpts.name,
            fieldName,
            filterOpts.value,
            filterOpts.operator,
          );
        }
      });
      this.currentPage = 1;
      this.updateBioMaterials();
    },
    async downloadExposureListing() {
      const id = this.userUid || this.spaceNid;
      if (!id) {
        this.$showNotification(
          "error",
          "Failed to download exposure listing. No user or space ID found.",
        );
        return;
      }
      const path = this.spaceNid
        ? `/raft/biologicals/node/${id}/exposure-listing.csv`
        : `/raft/biologicals/user/${id}/exposure-listing.csv`;
      const defaultFilename = this.spaceNid
        ? "space-exposure-listing.csv"
        : "user-exposure-listing.csv";
      // Need to remove the operative status filter to include past exposures
      const csvQueries = cloneDeep(JsonApiFilterManager.queries);
      this.deleteQueryFilter(csvQueries, "operative_status");
      const options = {
        path,
        queries: csvQueries,
        filename: defaultFilename,
        extraOptions: {},
        useResponseFilename: true,
      };
      try {
        this.isDownloading = true;
        await restClient.downloadFileStream(options);
        this.$showNotification(
          "success",
          "Successfully downloaded the exposure listing.",
        );
      } catch (error) {
        this.$showNotification("error", "Failed to download exposure listing.");
      } finally {
        this.isDownloading = false;
      }
    },
    deleteQueryFilter(queryObj, filterName) {
      const search = "filter[" + filterName + "]";
      Object.keys(queryObj)?.forEach((key) => {
        if (key.indexOf(search) === 0) {
          delete queryObj[key];
        }
      });
    },
    formatBioMaterialType(biomaterial, isLabel) {
      // Removes "bio_material--" from the beginning of the biomaterial type
      let name = biomaterial.replace(/^bio_material--/, "");

      // Take the last part after splitting by "--"
      name = name.split("--").pop();

      // If isLabel is true, replace "_" with space and capitalize the first letter
      if (isLabel) {
        name = name.replace(/_/g, " ");
        name = name.charAt(0).toUpperCase() + name.slice(1);
      }
      return name;
    },
  },
  created() {
    this.$store.commit("BiologicalsStore/setUserUid", this.userUid);
    this.$store.commit("BiologicalsStore/setSpaceNid", this.spaceNid);
  },
  async mounted() {
    await this.fetchBioSpaceListingOptions();
    this.setDefaultFilters();
    this.updateBioMaterials();
    await this.fetchUserExposureEntityDisplayData();
    this.componentReady = true;
  },
};
</script>

<style lang="css" scoped src="../../css/B3BaseTable.css"></style>
<style lang="css" scoped src="../../css/B3Pagination.css"></style>

<style scoped>
div.col {
  width: 30%;
  float: left;
}
</style>
